/** Shared Blog CMS types (client-safe). */
export type BlogStatus = "draft" | "review" | "scheduled" | "published" | "hidden" | "archived";

export type BlogSeo = {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage: string;
};

export type BlogAuthor = {
  id: string;
  slug: string;
  name: string;
  role: string;
  bio: string;
  avatarUrl: string;
};

export type BlogCategory = {
  id: string;
  slug: string;
  name: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
};

export type BlogTag = { id: string; slug: string; name: string };

export type BlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  gallery: string[];
  authorId: string | null;
  authorName: string;
  authorSlug: string;
  authorRole: string;
  categoryId: string | null;
  categoryName: string;
  categorySlug: string;
  tags: string[];
  readingMinutes: number;
  isFeatured: boolean;
  showOnHomepage: boolean;
  status: BlogStatus;
  publishedAt: string | null;
  scheduledAt: string | null;
  seo: BlogSeo;
  views: number;
  createdAt: string;
  updatedAt: string;
};

export type BlogPostInput = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  authorId: string | null;
  categoryId: string | null;
  tags: string[];
  readingMinutes: number;
  isFeatured: boolean;
  showOnHomepage: boolean;
  status: BlogStatus;
  publishedAt: string | null;
  scheduledAt: string | null;
  seo: BlogSeo;
};

export type BlogIndexData = {
  posts: BlogPost[];
  categories: BlogCategory[];
  tags: BlogTag[];
  authors: BlogAuthor[];
};

export type BlogPostDetail = {
  post: BlogPost;
  prev: { slug: string; title: string } | null;
  next: { slug: string; title: string } | null;
  related: BlogPost[];
};

export const EMPTY_SEO: BlogSeo = {
  title: "",
  description: "",
  keywords: "",
  canonical: "",
  ogImage: "",
};

export function estimateReadingMinutes(content: string): number {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}